import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import { trackPageView } from "@/lib/analytics";
import { Toaster } from "sonner";

import Discovery from "@/pages/Discovery";
import Brands from "@/pages/Brands";
import UnfairAdvantage from "@/pages/UnfairAdvantage";
import Management from "@/pages/Management";
import CreativeClass from "@/pages/CreativeClass";
import Distribution from "@/pages/Distribution";
import ArtizenPass from "@/pages/ArtizenPass";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import ArtistProfile from "@/pages/ArtistProfile";
import Admin from "@/pages/Admin";
import Education from "@/pages/Education";
import ComingSoon from "@/pages/ComingSoon";
import Editorial from "@/pages/Editorial";
import Releases from "@/pages/Releases";
import Commons from "@/pages/Commons";
import Inbox from "@/pages/Inbox";
import Create from "@/pages/Create";
import Build from "@/pages/Build";
import BuildArtist from "@/pages/BuildArtist";
import BuildBrand from "@/pages/BuildBrand";
import NoPressure from "@/pages/NoPressure";
import { About, Manifesto } from "@/pages/About";
import { Privacy, Terms, Refunds, Contact, NotFound } from "@/pages/Legal";

const COMING_SOON_ENV = process.env.REACT_APP_COMING_SOON === "true";

function isComingSoonHost() {
  // Production domain → landing form only.
  // Preview / localhost / anything else → full site.
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const isProd = host === "anartizen.com" || host.endsWith(".anartizen.com");
  const params = new URLSearchParams(window.location.search);
  // `?landing=1` force-shows the landing form anywhere (for QA + sharing previews of the form itself).
  if (params.get("landing") === "1") return true;
  // `?preview=1` overrides prod gating (so you can QA the live build through prod).
  if (params.get("preview") === "1") {
    try { localStorage.setItem("anartizen_preview", "1"); } catch (e) { /* noop */ }
    return false;
  }
  try {
    if (localStorage.getItem("anartizen_preview") === "1") return false;
  } catch (e) { /* noop */ }
  return isProd;
}

function AppRouter() {
  const location = useLocation();
  // Reset scroll to the top on every route change (except URL hash jumps and
  // the /unfair-advantage?advisor=SLUG deep-links which do their own scroll to
  // the booking panel). Fixes the "Grow" door landing mid-page.
  React.useEffect(() => {
    if (location.hash) return;
    if (location.pathname === "/unfair-advantage" && location.search.includes("advisor=")) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname, location.search, location.hash]);

  // GA4 page_view on every SPA route change. gtag's own send_page_view is
  // disabled in index.html so we don't double-count the initial load.
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const showLanding = COMING_SOON_ENV || isComingSoonHost();
  if (showLanding) {
    // Landing gate on production. Only these early-access routes escape it.
    return (
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/unfair-advantage" element={<UnfairAdvantage />} />
        <Route path="/no-pressure" element={<NoPressure />} />
        <Route path="/build" element={<Build />} />
        <Route path="/build/artist" element={<BuildArtist />} />
        <Route path="/build/brand" element={<BuildBrand />} />
        <Route path="/about" element={<About />} />
        <Route path="/manifesto" element={<Manifesto />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/artist/:slug" element={<ArtistProfile />} />
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<ComingSoon />} />
      <Route path="/discovery" element={<Discovery />} />
      <Route path="/lab" element={<Navigate to="/build" replace />} />
      <Route path="/brands" element={<Navigate to="/build" replace />} />
      <Route path="/services" element={<Navigate to="/build" replace />} />
      <Route path="/build" element={<Build />} />
      <Route path="/build/artist" element={<BuildArtist />} />
      <Route path="/build/brand" element={<BuildBrand />} />
      <Route path="/no-pressure" element={<NoPressure />} />
      <Route path="/unfair-advantage" element={<UnfairAdvantage />} />
      <Route path="/management" element={<Management />} />
      <Route path="/releases" element={<Releases />} />
      <Route path="/editorial" element={<Editorial />} />
      <Route path="/commons" element={<Commons />} />
      <Route path="/inbox" element={<Inbox />} />
      <Route path="/education" element={<Education />} />
      <Route path="/residency" element={<Education />} />
      <Route path="/academy" element={<Education />} />
      <Route path="/creative-class" element={<CreativeClass />} />
      <Route path="/distribution" element={<Distribution />} />
      {/* Membership canonical at /membership; /artizen-pass kept for back-compat */}
      <Route path="/membership" element={<ArtizenPass />} />
      <Route path="/artizen-pass" element={<Navigate to="/membership" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/artist/:slug" element={<ArtistProfile />} />
      <Route path="/about" element={<About />} />
      <Route path="/manifesto" element={<Manifesto />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partner" element={<Navigate to="/lab" replace />} />
      <Route path="/create" element={<Create />} />
      <Route path="/create/:type" element={<Create />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0a0a0a", border: "1px solid #222", color: "white", borderRadius: 0, fontFamily: "IBM Plex Mono" } }} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
